import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { APIStatus } from '@application/enums/api-status';
import { getAllRolesAsync, toggleRoleActiveAsync } from '@application/features/roles/rolesThunk';
import { useAppDispatch } from '@application/store/useStore';
import { alpha, Box, Button, IconButton, Switch, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import CustomDialog from '@ui/components/common/Dialog';
import { PrimaryGrid } from '@ui/components/common/Grid';
import ResponsiveSearch from '@ui/components/common/ResponsiveSearch';
import { PageLayout } from '@ui/components/PageLayout';
import { centered, centeredRowStack } from '@ui/style/style';
import { AddCircle } from 'iconsax-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import RoleDialog from './RoleDialog';
import AddEditRoleForm from './RoleDialog/AddEditRoleForm';
import useRolesStyles from './useRolesStyles';
import { hasPermission } from '@application/helpers/permission-helper';
import { resetSearchString, setSearchString } from '@application/features/roles/rolesSlice';
import BDrawer from '@ui/components/common/BDrawer';
import { Lock } from 'iconsax-react';
import { Iconly } from 'react-iconly';
const RolePermissions = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { roles, status, searchString } = useSelector((state) => state.role);
    const selectedCompanyId = useSelector((state) => state.company.selectedCompanyId);
    const loggedInUser = useSelector((state) => state.auth.currentUser);
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const styles = useRolesStyles();
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });
    const [openAddRoleDialog, setOpenAddRoleDialog] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [switchDialogOpen, setSwitchDialogOpen] = useState(false);
    const [switchTargetRow, setSwitchTargetRow] = useState(null);
    const canReadRole = hasPermission(loggedInUser?.permissions, 'Permissions.Roles.Read');
    const canCreateRole = hasPermission(loggedInUser?.permissions, 'Permissions.Roles.Create');
    const canUpdateRole = hasPermission(loggedInUser?.permissions, 'Permissions.Roles.Update');
    useEffect(() => {
        const request = {
            pageNumber: paginationModel.page + 1,
            pageSize: paginationModel.pageSize,
            searchString: searchString
        };
        dispatch(getAllRolesAsync(request));
        // return () => {
        //   dispatch(resetSearchString());
        // };
    }, [dispatch, paginationModel.page, paginationModel.pageSize, selectedCompanyId, searchString]);
    const handleToggleRoleActive = async (roleId) => {
        await dispatch(toggleRoleActiveAsync(roleId))
            .unwrap()
            .then((response) => {
            if (response.isSuccess) {
                toast.success(response.message);
            }
            else {
                toast.error(response.message);
            }
        });
    };
    const handleAddRole = () => {
        // Navigate directly to the combined role and permissions screen
        navigate('/roles-permissions/new');
    };
    const rows = roles?.items.map((role) => ({
        id: role.id,
        role: role.displayName,
        company: role.companyName,
        companies: role.companies || [],
        companyCount: role.companyCount || 0,
        users: role.userCount,
        active: role.isActive,
        isSystem: role.isSystem
    })) || [];
    const rolesColumns = [
        {
            field: 'role',
            type: 'string',
            sortable: false,
            headerName: t('labels.role'),
            flex: 1,
            minWidth: 200
        },
        {
            field: 'companyCount',
            headerName: t('labels.companyCount'),
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                const companies = params.row.companies || [];
                const companyCount = params.value || 0;
                if (companyCount === 0) {
                    return '—';
                }
                const companyNames = companies.map((c) => c.name);
                return (_jsx(Tooltip, { title: _jsx(Box, { sx: { maxWidth: 300, maxHeight: 150, overflow: 'auto' }, children: companyNames.join(', ') }), arrow: true, children: _jsx(Box, { display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 32, minHeight: 32, borderRadius: '500px', sx: { backgroundColor: alpha(theme.palette.info.dark, 0.08) }, color: theme.palette.info.dark, children: _jsx(Typography, { variant: 'body2', color: theme.palette.info.dark, style: { textAlign: 'center' }, children: companyCount }) }) }));
            }
        },
        {
            field: 'users',
            headerName: t('common.users'),
            headerAlign: 'right',
            align: 'right',
            sortable: false,
            flex: 1,
            minWidth: 200,
            renderCell: (params) => {
                return (_jsx(Box, { display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 32, minHeight: 32, borderRadius: '500px', sx: { backgroundColor: alpha(theme.palette.success.main, 0.08) }, color: theme.palette.success.main, children: _jsx(Typography, { variant: 'body2', color: theme.palette.success.main, style: { textAlign: 'center' }, children: params.value }) }));
            }
        },
        {
            field: 'active',
            headerName: 'Active',
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => (_jsx(Box, { onClick: (event) => event.stopPropagation(), children: _jsx(Switch, { size: 'medium', checked: params.row.active, disabled: params.row.isSystem || !canUpdateRole, onChange: () => {
                        setSwitchTargetRow(params.row);
                        setSwitchDialogOpen(true);
                    } }) }))
        },
        {
            field: 'edit',
            headerName: t('rolesPermissions.editPermissions'),
            width: 200,
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (_jsx(Box, { sx: { ...centered, height: '100%', width: '100%' }, gap: 1, onClick: (event) => event.stopPropagation(), children: _jsx(IconButton, { size: 'small', onClick: (event) => {
                        event.stopPropagation();
                        navigate(`/roles-permissions/${params.row.id}/permissions`);
                    }, disabled: params.row.isSystem || !canUpdateRole, sx: {
                        opacity: params.row.isSystem || !canUpdateRole ? 0.5 : 1
                    }, children: _jsx(Iconly, { name: 'Edit', set: 'curved', primaryColor: theme.palette.text.primary }) }) }))
        }
    ];
    return (_jsxs(PageLayout, { title: t('common.rolesPermissions'), secondTitle: t('rolesPermissions.manageUserRolesAndPermissionsSecurely'), showLeftIcon: true, leftIcon: _jsx(Lock, { variant: 'Outline' }), rightContent: _jsx(Button, { disabled: !canCreateRole, startIcon: _jsx(AddCircle, {}), variant: 'contained', fullWidth: isSmallScreen, size: isSmallScreen ? 'medium' : 'large', color: 'primary', onClick: handleAddRole, children: t('rolesPermissions.addRole') }), children: [isSmallScreen ? (_jsx(_Fragment, { children: _jsx(BDrawer, { anchor: 'bottom', title: t('rolesPermissions.addRole'), open: drawerOpen, onClose: () => {
                        setDrawerOpen(false);
                    }, children: _jsx(AddEditRoleForm, { edit: false, onCancel: () => setDrawerOpen(false), onSubmit: handleAddRole }) }) })) : (_jsx(Box, { children: openAddRoleDialog && (_jsx(RoleDialog, { open: openAddRoleDialog, onClose: () => {
                        setOpenAddRoleDialog(false);
                    }, onSubmitRole: handleAddRole })) })), canReadRole ? (
            // <Box sx={{ ...styles.roleContainer }}>
            _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: '12px', children: [_jsx(Box, { display: 'flex', justifyContent: 'flex-end', children: _jsx(ResponsiveSearch, { value: searchString || '', onChange: (value) => dispatch(setSearchString(value)), placeholder: t('rolesPermissions.searchRoles'), onClear: () => dispatch(resetSearchString()) }) }), _jsx(PrimaryGrid, { rows: rows, columns: rolesColumns, haveSearch: false, maxHeight: 'calc(100dvh - 180px)', showFilters: false, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, rowCount: roles?.totalCount || 0, paginationMode: 'server', loading: status === APIStatus.PENDING, disableRowSelectionOnClick: true, showHeaderControls: false })] })) : (
            // </Box>
            _jsx(Typography, { color: 'error', sx: { mt: 4 }, children: t('common.noPermissions') })), switchDialogOpen && switchTargetRow && (_jsx(CustomDialog, { backgroundVariant: 'white', open: switchDialogOpen, title: switchTargetRow.active
                    ? t('rolesPermissions.deactivateRole')
                    : t('rolesPermissions.activateRole'), onClose: () => setSwitchDialogOpen(false), actions: _jsxs(Box, { sx: centeredRowStack, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: () => setSwitchDialogOpen(false), children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'inherit', fullWidth: true, onClick: () => {
                                handleToggleRoleActive(switchTargetRow.id);
                                setSwitchDialogOpen(false);
                            }, children: t('actions.confirm') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: switchTargetRow.active
                            ? t('rolesPermissions.deactivationConfirm')
                            : t('rolesPermissions.activationConfirm') }) }) }))] }));
};
export default RolePermissions;
